import Group from 'components/utils/Group'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import isHTMLInputElement from 'lib/dom/isHTMLInputElement'
import LoyaltyStayUpgradeRadioOption from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeRadioOption'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import React, { FormEventHandler, useCallback, useContext, useState } from 'react'
import { useIntl } from 'react-intl'

const OPTIONS_NAME = 'upgrade-option'

interface Props {
  originPackage: App.HotelPackage
  upgradeAvailabilityView: App.OfferPackageOptionLoyaltyUpgradeRoomAndPackageOnlyAvailabilityView
  selectedTargetUpgradeType: App.LoyaltyStayUpgradeType | undefined
}

interface Result {
  upgradeType: App.LoyaltyStayUpgradeType | undefined
}

export type OfferPageLoyaltyStayUpgradeModalResult = Result | undefined

function OfferPageLoyaltyStayUpgradeModal({ originPackage, upgradeAvailabilityView, selectedTargetUpgradeType }: Props) {
  const modalElement = useModalElementContext<OfferPageLoyaltyStayUpgradeModalResult>()
  const { offer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)
  const intl = useIntl()
  const removeUpgrade = useCallback(() => {
    modalElement.resolve({ upgradeType: undefined })
  }, [modalElement])

  const [selectedOption, setSelectedOption] = useState<App.LoyaltyStayUpgradeType>(selectedTargetUpgradeType ?? 'package')

  const handleChange = useCallback<FormEventHandler<HTMLFieldSetElement>>((event) => {
    if (isHTMLInputElement(event.target)) {
      setSelectedOption(event.target.value as App.LoyaltyStayUpgradeType)
    }
  }, [])

  const applyUpgrade = useCallback(() => {
    modalElement.resolve({ upgradeType: selectedOption })
  }, [modalElement, selectedOption])

  const modalTitle = intl.formatMessage({
    id: 'yj5zQgAgde',
    defaultMessage: 'Upgrade your stay',
    description: 'Modal title for loyalty stay upgrade selection',
  })

  const applyFreePackageUpgradeLabel = intl.formatMessage({
    id: 'axCJUWmqNR',
    defaultMessage: 'Apply free package upgrade',
    description: 'Primary button text to apply free package upgrade in loyalty upgrade modal',
  })

  const applyFreeRoomUpgradeLabel = intl.formatMessage({
    id: 'IQ8YmfED/u',
    defaultMessage: 'Apply free room upgrade',
    description: 'Primary button text to apply free room upgrade in loyalty upgrade modal',
  })

  const removeLabel = intl.formatMessage({
    id: 'WrZFStqTqW',
    defaultMessage: 'Remove',
    description: 'Secondary button text to remove applied loyalty stay upgrade',
  })

  const chooseUpgradeLabel = intl.formatMessage({
    id: 'ojSSAdjw73',
    defaultMessage: 'Choose from room or package upgrade',
    description: 'Body text instructing user to choose between room or package upgrade in loyalty upgrade modal',
  })

  return <Modal
    title={modalTitle}
    size="L"
    primaryActionText={<>
      {selectedOption === 'package' && applyFreePackageUpgradeLabel}
      {selectedOption === 'room' && applyFreeRoomUpgradeLabel}
    </>}
    onPrimaryActionClick={applyUpgrade}
    isSecondaryActionHidden={!selectedTargetUpgradeType}
    secondaryActionText={removeLabel}
    onSecondaryActionClick={removeUpgrade}
  >
    <Group direction="vertical" gap={16}>
      <BodyText variant="large" colour="neutral-two">
        {chooseUpgradeLabel}
      </BodyText>
      <fieldset onChange={handleChange}>
        <Group direction="vertical" gap={16}>
          <LoyaltyStayUpgradeRadioOption
            name={OPTIONS_NAME}
            value="package"
            variant="highlight"
            checked={selectedOption === 'package'}
            originPackage={originPackage}
            targetPackage={upgradeAvailabilityView.packageUpgradePackage}
            offer={offer}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
          />
          <LoyaltyStayUpgradeRadioOption
            name={OPTIONS_NAME}
            value="room"
            checked={selectedOption === 'room'}
            originPackage={originPackage}
            targetPackage={upgradeAvailabilityView.roomUpgradePackage}
            offer={offer}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
          />
        </Group>
      </fieldset>
    </Group>
  </Modal>
}

export default OfferPageLoyaltyStayUpgradeModal
